import { Injectable, inject } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { APIServiceName, ApiService, Common, CommonApiResponse } from '@techextensor/tab-core-utility';
import { TabSdk } from '@techextensor/tab-sdk';
import { VersioningApi, VersioningServiceName } from '../const/constants';
import {
  CiResults,
  Deployment,
  EnvironmentPolicy,
  EnvironmentState,
  HistoryEntry,
  PipelineJob,
  Release,
  ReviewCommit,
  ReviewDetail,
  ReviewSummary,
  VERSIONING_NOT_SET_UP,
  VersionRole,
  VersionTag,
  jwtSubject,
  rolesOf,
  toCiResults,
  toDeployment,
  toEnvironmentPolicy,
  toEnvironmentState,
  toHistoryEntry,
  toPipelineJob,
  toRelease,
  toReviewCommit,
  toReviewDetail,
  toReviewSummary,
  toVersionTag,
  versioningKeys
} from '../utils/version-history.utils';

/** The end of a job's log (`JobLogDto`). */
export interface JobLog {
  content: string;
  truncated: boolean;
}

/** One file of a tree (`TreeResultDto`). */
export interface TreeListing {
  sha: string;
  files: { path: string; blobSha: string }[];
}

/**
 * Reads the current app's version history from the versioning service (taf_versioningservice), and sends the few
 * actions the pages offer. The repository is the signed-in tenant + app (`versioningKeys`).
 *
 * Every call goes through core-utility's `ApiService`, so the auth interceptor attaches the bearer token and the
 * TenantId / AppId / EnvironmentId headers, and the answer comes back normalised (`IsSuccess` / `Result`). A refused
 * call rejects with the HttpErrorResponse (status + the service's `{ error: { code, message } }`), which
 * `failureWords` turns into a sentence. Who may do what is decided by the service; the pages only hide what the
 * person's version roles cannot do.
 */
@Injectable({ providedIn: 'root' })
export class VersionHistoryService {
  private readonly _api = inject(ApiService);

  /** Whether an environment configures the service's address (`apiServiceUrls.versioningService`). */
  isConfigured(): boolean {
    const url = (Common.apiServiceUrls as unknown as Record<string, unknown> | undefined)?.[VersioningServiceName];
    return typeof url === 'string' && url.trim().length > 0;
  }

  /** The repository of the signed-in tenant + app, or null when either id is missing. */
  keys(): { space: string; repo: string } | null {
    return versioningKeys(TabSdk.store?.tenantId, TabSdk.store?.appId);
  }

  /** The signed-in person as the service knows them (the token's `sub`). */
  me(): string | null {
    return jwtSubject(TabSdk.store?.token);
  }

  async myRoles(): Promise<Set<VersionRole>> {
    const me = this.me();
    if (!me) return new Set();
    return rolesOf(await this.get<any[]>(VersioningApi.members), me);
  }

  // ---- reviews -----------------------------------------------------------------------------------------------------

  async reviews(state?: 'open' | 'merged' | 'closed'): Promise<ReviewSummary[]> {
    const params = state ? new HttpParams().set('state', state) : undefined;
    return this.list(await this.get<any[]>(VersioningApi.reviews, params)).map(toReviewSummary);
  }

  async review(id: string): Promise<ReviewDetail> {
    return toReviewDetail(await this.get<any>(VersioningApi.review(id)));
  }

  async reviewChecks(id: string): Promise<CiResults> {
    return toCiResults(await this.get<any>(VersioningApi.reviewCiResults(id)));
  }

  /** One page of a review's commits, newest first; an empty list from a service without the route. */
  async reviewCommits(id: string, limit = 100, page = 1): Promise<ReviewCommit[]> {
    const params = new HttpParams().set('limit', String(limit)).set('page', String(page));
    return this.list(await this.optional(() => this.get<any[]>(VersioningApi.reviewCommits(id), params), [])).map(toReviewCommit);
  }

  async decide(id: string, decision: 'approve' | 'request-changes', note?: string): Promise<void> {
    await this.post(VersioningApi.reviewApprovals(id), note ? { decision, note } : { decision });
  }

  /** Every file at a ref (a branch or a commit). */
  async tree(ref: string): Promise<TreeListing> {
    const raw = await this.get<any>(VersioningApi.tree, new HttpParams().set('ref', ref));
    return {
      sha: String(raw?.sha ?? ''),
      files: this.list(raw?.files).map(f => ({ path: String(f?.path ?? ''), blobSha: String(f?.blobSha ?? '') }))
    };
  }

  /** One page of the main version's saved changes, newest first. */
  async history(limit = 20, page = 1): Promise<HistoryEntry[]> {
    const params = new HttpParams().set('limit', String(limit)).set('page', String(page));
    return this.list(await this.get<any[]>(VersioningApi.history, params)).map(toHistoryEntry);
  }

  /** The end of a job's log (needs the Deploy permission). */
  async jobLog(jobId: string): Promise<JobLog> {
    const raw = await this.get<any>(VersioningApi.jobTrace(jobId));
    return { content: String(raw?.content ?? ''), truncated: raw?.truncated === true };
  }

  /** The jobs of a pipeline (needs the Deploy permission). */
  async pipelineJobs(pipelineId: string): Promise<PipelineJob[]> {
    return this.list(await this.get<any[]>(VersioningApi.pipelineJobs(pipelineId))).map(toPipelineJob);
  }

  // ---- releases and deployments ------------------------------------------------------------------------------------

  async releases(): Promise<Release[]> {
    return this.list(await this.get<any[]>(VersioningApi.releases)).map(toRelease);
  }

  /** Version tags; an empty list from a service without the route. */
  async versions(): Promise<VersionTag[]> {
    return this.list(await this.optional(() => this.get<any[]>(VersioningApi.versions), [])).map(toVersionTag);
  }

  async environments(): Promise<EnvironmentState[]> {
    return this.list(await this.get<any[]>(VersioningApi.environments)).map(toEnvironmentState);
  }

  async deployments(environment?: string, limit = 50): Promise<Deployment[]> {
    let params = new HttpParams().set('limit', String(limit));
    if (environment) params = params.set('environment', environment);
    return this.list(await this.get<any[]>(VersioningApi.deployments, params)).map(toDeployment);
  }

  /** Environment policies; an empty list from a service without the route. */
  async policies(): Promise<EnvironmentPolicy[]> {
    return this.list(await this.optional(() => this.get<any[]>(VersioningApi.environmentPolicies), [])).map(toEnvironmentPolicy);
  }

  /** The promotion path in order; an empty list when none is set or the service has no such route. */
  async promotionPath(): Promise<string[]> {
    const raw = await this.optional(() => this.get<any>(VersioningApi.promotionPath), null);
    return this.list(raw?.environments).filter((e): e is string => typeof e === 'string' && !!e.trim());
  }

  /** Deploys a released version to an environment: promote or roll back. */
  async deploy(environment: string, version: string): Promise<Deployment> {
    return toDeployment(await this.post(VersioningApi.deployments, { environment, version }));
  }

  async approveDeployment(id: string): Promise<Deployment> {
    return toDeployment(await this.post(VersioningApi.deploymentApprove(id), {}));
  }

  async rejectDeployment(id: string): Promise<Deployment> {
    return toDeployment(await this.post(VersioningApi.deploymentReject(id), {}));
  }

  // ---- plumbing ----------------------------------------------------------------------------------------------------

  private path(endpoint: string): string {
    const keys = this.keys();
    if (!this.isConfigured() || !keys) throw { code: VERSIONING_NOT_SET_UP };
    return `${VersioningApi.repository(keys.space, keys.repo)}/${endpoint}`;
  }

  private async get<T>(endpoint: string, params?: HttpParams): Promise<T> {
    const path = this.path(endpoint);
    return this.unwrap<T>(await firstValueFrom(this._api.getFromService(VersioningServiceName as APIServiceName, path, params)));
  }

  private async post<T = any>(endpoint: string, body: unknown): Promise<T> {
    const path = this.path(endpoint);
    return this.unwrap<T>(await firstValueFrom(this._api.postToService(VersioningServiceName as APIServiceName, path, body)));
  }

  /** A route only some deployments of the service have: its 404 (or 405) reads as `fallback`. */
  private async optional<T>(call: () => Promise<T>, fallback: T): Promise<T> {
    try {
      return await call();
    } catch (error: any) {
      if (error?.status === 404 || error?.status === 405) return fallback;
      throw error;
    }
  }

  private unwrap<T>(response: CommonApiResponse): T {
    if (!response?.IsSuccess) {
      throw { status: 200, error: { error: { code: null, message: response?.Errors?.[0] ?? null } } };
    }
    return response.Result as T;
  }

  private list(value: unknown): any[] {
    return Array.isArray(value) ? value : [];
  }
}
